import { Component } from 'react';
import { AlertTriangle, RotateCcw, RefreshCw } from 'lucide-react';
import { api } from '../api';

/**
 * Poistka proti prázdnej stránke.
 *
 * Keď niektorá časť rozhrania spadne pri vykresľovaní, React zahodí **celý**
 * strom – zmizne aj bočné menu a ostane prázdna plocha. Používateľ nevie, čo sa
 * stalo, a musí stránku obnoviť. Táto poistka chybu zachytí, ukáže zrozumiteľnú
 * hlášku s tlačidlom „Skúsiť znova" a chybu zapíše na server, aby sa dala nájsť
 * a opraviť – bez nej sa o nej nikto nedozvie.
 *
 * Je to trieda, nie funkcia: zachytiť chybu vykresľovania vie v Reacte iba
 * komponent s `componentDidCatch`.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { chyba: null, detail: false };
  }

  static getDerivedStateFromError(chyba) {
    return { chyba };
  }

  componentDidCatch(chyba, info) {
    // Zápis je „nech sa podarí" – keď server nedosiahneme, hláška aj tak platí.
    try {
      /* Meno súboru s kódom je jediné, čo spoľahlivo povie, **ktorá verzia**
       * spadla. Bez neho sa pri stránke, ktorú má niekto otvorenú od včera,
       * hľadá chyba vo verzii, ktorá už dávno neplatí. */
      const skript = [...document.querySelectorAll('script[src]')]
        .map((x) => x.src).find((x) => /assets\/index-/.test(x)) || '';
      api.post('/api/stats/chyba-rozhrania', {
        sprava: String(chyba?.message || chyba),
        stack: String(chyba?.stack || '').slice(0, 4000),
        komponent: String(info?.componentStack || '').slice(0, 2000),
        adresa: window.location.pathname + window.location.search,
        balik: skript.split('/').pop(),
      }).catch(() => {});
    } catch {}
    console.error('Chyba rozhrania:', chyba, info);
  }

  /* Pri prechode na inú stránku sa poistka zhodí, nech sa systém nezasekne na
   * chybe, ktorá sa týkala len predchádzajúcej obrazovky. */
  componentDidUpdate(predch) {
    if (this.state.chyba && predch.kluc !== this.props.kluc) {
      this.setState({ chyba: null, detail: false });
    }
  }

  render() {
    const { chyba, detail } = this.state;
    if (!chyba) return this.props.children;

    return (
      <div className="mx-auto max-w-xl py-10">
        <div className="card-p text-center">
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-amber-50 text-amber-600">
            <AlertTriangle size={22} />
          </div>
          <h2 className="text-lg font-semibold text-slate-800">Túto obrazovku sa nepodarilo zobraziť</h2>
          <p className="mt-1 text-sm text-slate-500">
            Nič sa nepokazilo v údajoch – zlyhalo iba vykreslenie stránky.
            Skúste to znova, prípadne obnovte stránku.
          </p>

          <div className="mt-5 flex justify-center gap-2">
            <button className="btn bg-brand-600 text-white hover:bg-brand-500"
              onClick={() => this.setState({ chyba: null, detail: false })}>
              <RotateCcw size={16} /> Skúsiť znova
            </button>
            <button className="btn border border-slate-200 bg-white"
              onClick={() => window.location.reload()}>
              <RefreshCw size={16} /> Obnoviť stránku
            </button>
          </div>

          <button className="mt-4 text-xs text-slate-400 hover:text-slate-600"
            onClick={() => this.setState({ detail: !detail })}>
            {detail ? 'Skryť podrobnosti' : 'Zobraziť podrobnosti'}
          </button>
          {detail && (
            <pre className="mt-2 max-h-52 overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-left text-[11px] leading-relaxed text-slate-600">
              {String(chyba?.message || chyba)}
              {chyba?.stack ? `\n\n${chyba.stack}` : ''}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
